import React, { useEffect, useState } from "react";
import apiClient, { formatApiError } from "@/api/client";
import { ShieldCheck, Loader2, Wallet, LineChart, UserPlus, FileCheck2, Users, Briefcase, ClipboardList, Globe, GraduationCap, UserCog, LifeBuoy, MessagesSquare, CalendarDays, Calendar, DollarSign, Plus, Trash2, FileDown, Link2, CheckCircle2, AlertCircle, ExternalLink, Mail, Edit2, RotateCcw, Send, Bell, BellOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";

const MODULE_TOGGLES = [
  { key: "members_can_see_customers", label: "Müşteriler", icon: Users },
  { key: "members_can_see_services", label: "Hizmetler", icon: Briefcase },
  { key: "members_can_see_tasks", label: "Görevler", icon: ClipboardList },
  { key: "members_can_see_meetings", label: "Toplantılar", icon: CalendarDays },
  { key: "members_can_see_calendar", label: "Takvim", icon: Calendar },
  { key: "members_can_see_leads", label: "Leadler", icon: UserPlus },
  { key: "members_can_see_tickets", label: "Destek", icon: LifeBuoy },
  { key: "members_can_see_messages", label: "Mesajlar", icon: MessagesSquare },
  { key: "members_can_see_domains", label: "Domain", icon: Globe },
  { key: "members_can_see_students", label: "Eğitim", icon: GraduationCap },
  { key: "members_can_see_team", label: "Ekip", icon: UserCog },
];
const SENSITIVE_TOGGLES = [
  { key: "members_can_see_revenue", label: "Gelir", icon: DollarSign },
  { key: "members_can_see_ads", label: "Google Ads", icon: LineChart },
  { key: "members_can_see_finance", label: "Finans", icon: FileCheck2 },
];
const ACTION_TOGGLES = [
  { key: "members_can_create_customer", label: "Müşteri ekleyebilir", icon: Plus },
  { key: "members_can_delete_records", label: "Kayıt silebilir", icon: Trash2 },
  { key: "members_can_export", label: "CSV/Rapor dışa aktarabilir", icon: FileDown },
];

// Reference list of all dynamic variables available in email templates.
// Keep in sync with backend _build_default_email_ctx() and per-trigger contexts.
const DYNAMIC_VAR_GROUPS = [
  { title: "Ortak / Marka", vars: [
    { key: "agency_name", desc: "Ajans tam adı (Kamil Yıldırım Dijital Pazarlama)" },
    { key: "agency_short", desc: "Ajans kısa adı (Kamil Yıldırım)" },
    { key: "app_url", desc: "CRM ana adresi" },
    { key: "portal_url", desc: "Müşteri portalı giriş linki" },
    { key: "dashboard_url", desc: "İç panel ana sayfa" },
  ]},
  { title: "Müşteri Bilgileri", vars: [
    { key: "customer_name", desc: "Müşterinin adı/soyadı" },
    { key: "customer_email", desc: "Müşterinin e-posta adresi" },
    { key: "company", desc: "Müşterinin şirket/firma adı" },
    { key: "portal_password", desc: "İlk giriş için geçici şifre" },
  ]},
  { title: "Hizmet & Görev", vars: [
    { key: "service_title", desc: "Hizmet/proje adı (ör. SEO Çalışması)" },
    { key: "duration", desc: "Tahmini süre (boşsa kutu gizlenir)" },
    { key: "summary", desc: "Özet / tamamlanma notları" },
    { key: "task_title", desc: "Görev başlığı" },
    { key: "task_link", desc: "Göreve gitme linki" },
    { key: "priority", desc: "Görev önceliği (düşük/orta/yüksek)" },
    { key: "due_date", desc: "Son tarih" },
  ]},
  { title: "Domain", vars: [
    { key: "domain", desc: "Alan adı (ör. example.com)" },
    { key: "expires_at", desc: "Domain bitiş tarihi" },
  ]},
  { title: "Toplantı / Lead", vars: [
    { key: "meeting_title", desc: "Toplantı başlığı" },
    { key: "start_time", desc: "Toplantı başlangıç saati" },
    { key: "meeting_url", desc: "Google Meet linki (boşsa buton gizlenir)" },
    { key: "lead_name", desc: "Lead adı" },
    { key: "lead_link", desc: "Lead profili linki" },
    { key: "source", desc: "Lead kaynağı" },
    { key: "interested_service", desc: "Lead'in ilgilendiği hizmet" },
  ]},
  { title: "Mesaj / Yorum", vars: [
    { key: "user_name", desc: "Maili alan ekip üyesinin adı" },
    { key: "sender_name", desc: "Mesajı gönderen kişi" },
    { key: "commenter_name", desc: "Yorumu yapan kişi" },
    { key: "preview", desc: "Mesaj/yorum önizleme metni" },
    { key: "message_link", desc: "Mesajlar sayfası linki" },
  ]},
  { title: "Fatura & Ödeme", vars: [
    { key: "invoice_no", desc: "Fatura numarası" },
    { key: "amount", desc: "Tutar (ör. 15.000 ₺)" },
    { key: "due_date", desc: "Fatura vade tarihi" },
  ]},
  { title: "Destek (Ticket)", vars: [
    { key: "ticket_id", desc: "Destek talebi ID" },
    { key: "subject", desc: "Ticket konusu" },
    { key: "reply_preview", desc: "Yanıt önizleme metni" },
  ]},
  { title: "Eğitim / Öğrenci", vars: [
    { key: "course", desc: "Kurs adı (Google Ads / Meta / Karma)" },
    { key: "lesson_date", desc: "Ders tarihi/saati" },
  ]},
  { title: "Rapor", vars: [
    { key: "week_label", desc: "Hafta etiketi (ör. 13–19 Mayıs)" },
    { key: "month_label", desc: "Ay etiketi (ör. Mayıs 2026)" },
    { key: "report_url", desc: "Tam rapor PDF linki" },
  ]},
  { title: "Uyarı / Diğer", vars: [
    { key: "title", desc: "Genel başlık (kritik uyarı)" },
    { key: "message", desc: "Detay mesajı" },
    { key: "reset_url", desc: "Şifre sıfırlama linki" },
  ]},
];

export default function AdminSettingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("roles");

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="space-y-5" data-testid="admin-settings-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck size={22} className="text-[#4F46E5]"/>Yönetim Ayarları</h1>
        <p className="text-sm text-[#64748B] mt-1">Roller & izinler, e-posta servisi/şablonları, Google Workspace</p>
      </div>

      <div className="flex gap-1 border-b border-[#ECECEF] overflow-x-auto">
        {[["roles","Roller & İzinler"],["mail","Mail Sistemi"],["integrations","Entegrasyonlar"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${tab===k?"border-[#4F46E5] text-[#4F46E5]":"border-transparent text-[#64748B] hover:text-[#0F172A]"}`} data-testid={`admin-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "roles" && <RolesPanel />}
      {tab === "mail" && <MailPanel />}
      {tab === "integrations" && <IntegrationsPanel />}
    </div>
  );
}

function Toggle({ on, onClick, label, desc, icon: Icon, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition text-left ${on ? "border-[#4F46E5] bg-[#EEF2FF]" : "border-[#ECECEF] bg-white hover:bg-[#FAFAFB]"}`}>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${on ? "bg-[#4F46E5] text-white" : "bg-[#F4F4F5] text-[#94A3B8]"}`}>
        <Icon size={13}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium">{label}</div>
        {desc && <div className="text-[10px] text-[#94A3B8] mt-0.5">{desc}</div>}
      </div>
      <div className={`relative w-9 h-5 rounded-full transition shrink-0 ${on ? "bg-[#4F46E5]" : "bg-[#DEDEE3]"}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? "left-[18px]" : "left-0.5"}`}/>
      </div>
    </button>
  );
}

function RolesPanel() {
  const [data, setData] = useState(null);
  const [selectedRole, setSelectedRole] = useState("project_manager");
  const [saving, setSaving] = useState(false);

  useEffect(() => { apiClient.get("/admin/roles").then(r => setData(r.data)); }, []);

  if (!data) return <div className="text-sm text-[#94A3B8]">Yükleniyor…</div>;
  const perms = data.permissions[selectedRole] || {};

  const toggle = async (k) => {
    const next = { ...perms, [k]: !perms[k] };
    setData({ ...data, permissions: { ...data.permissions, [selectedRole]: next } });
    setSaving(true);
    try { await apiClient.put(`/admin/roles/${selectedRole}`, next); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-5" data-testid="roles-panel">
      <div className="ky-card p-3 h-fit">
        <div className="text-xs uppercase tracking-wider text-[#64748B] font-semibold px-2 py-1.5">Roller</div>
        {data.roles.filter(r => r.key !== "admin").map(r => (
          <button key={r.key} onClick={() => setSelectedRole(r.key)} className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between ${selectedRole===r.key?"bg-[#EEF2FF] text-[#4F46E5] font-medium":"hover:bg-[#FAFAFB]"}`} data-testid={`role-${r.key}`}>
            <span>{r.label}</span>
            {r.system && <span className="ky-badge ky-badge-neutral">Sistem</span>}
          </button>
        ))}
        <div className="mt-3 px-3 py-2 bg-[#FAFAFB] rounded-md text-[10px] text-[#94A3B8] leading-relaxed">
          <strong>Admin</strong> tüm modüllere erişir, ayrıca yapılandırılamaz.
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-base">{data.roles.find(r => r.key === selectedRole)?.label} — İzinler</h3>
          {saving && <span className="text-xs text-[#94A3B8] flex items-center gap-1"><Loader2 size={11} className="animate-spin"/>Kaydediliyor…</span>}
        </div>
        <PermGroup title="Modül Görünürlüğü" desc="Sol menüde hangi modüller görünsün?" items={MODULE_TOGGLES} perms={perms} onToggle={toggle}/>
        <PermGroup title="Hassas Modüller" desc="Finansal ve reklam verileri." items={SENSITIVE_TOGGLES} perms={perms} onToggle={toggle}/>
        <PermGroup title="Eylem İzinleri" desc="Ne yapabilsin?" items={ACTION_TOGGLES} perms={perms} onToggle={toggle}/>
      </div>
    </div>
  );
}

function PermGroup({ title, desc, items, perms, onToggle }) {
  return (
    <div className="ky-card p-4">
      <h4 className="font-semibold text-sm">{title}</h4>
      <p className="text-[11px] text-[#94A3B8] mt-0.5">{desc}</p>
      <div className="grid sm:grid-cols-2 gap-2 mt-3">
        {items.map(t => <Toggle key={t.key} on={!!perms[t.key]} onClick={() => onToggle(t.key)} label={t.label} icon={t.icon} testid={`perm-${t.key}`}/>)}
      </div>
    </div>
  );
}

function MailPanel() {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [audience, setAudience] = useState("customer");
  const [editing, setEditing] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  const load = () => Promise.all([
    apiClient.get("/email/settings").then(r=>r.data),
    apiClient.get("/email/templates").then(r=>r.data),
  ]).then(([s,t])=>{ setSettings(s); setTemplates(t); });
  useEffect(()=>{ load(); }, []);

  const saveSettings = async () => {
    try { await apiClient.put("/email/settings", { active_service: settings.active_service, from_name: settings.from_name, from_email: settings.from_email, send_user_id: settings.send_user_id || null }); toast.success("Kaydedildi"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // v14: instant-save notification kill switches (no separate "save" button needed).
  const toggleNotification = async (field, value) => {
    const next = { ...settings, [field]: value };
    setSettings(next);
    try {
      await apiClient.put("/email/settings", { active_service: next.active_service, [field]: value });
      const labels = {
        notifications_enabled: value ? "Tüm bildirimler açıldı" : "Tüm bildirimler kapatıldı",
        team_notifications_enabled: value ? "Ekip bildirimleri açıldı" : "Ekip bildirimleri kapatıldı",
        customer_notifications_enabled: value ? "Müşteri bildirimleri açıldı" : "Müşteri bildirimleri kapatıldı",
      };
      toast.success(labels[field]);
    } catch (e) {
      // revert on error
      setSettings(settings);
      toast.error(formatApiError(e));
    }
  };
  const saveTemplate = async () => {
    if (!editing) return;
    try { await apiClient.put(`/email/templates/${editing.id}`, { key: editing.key, audience: editing.audience, name: editing.name, subject: editing.subject, body_html: editing.body_html, enabled: editing.enabled }); toast.success("Şablon kaydedildi"); setEditing(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const resetTemplate = async () => {
    if (!editing) return;
    if (!window.confirm("Varsayılana sıfırlansın mı?")) return;
    await apiClient.post(`/email/templates/${editing.id}/reset`); toast.success("Sıfırlandı"); setEditing(null); load();
  };
  const loadLogs = async () => { const { data } = await apiClient.get("/email/logs"); setLogs(data); setShowLogs(true); };

  if (!settings) return <div className="text-sm text-[#94A3B8]">Yükleniyor…</div>;
  const filtered = templates.filter(t => t.audience === audience);

  return (
    <div className="space-y-5" data-testid="mail-panel">
      {/* v14: Notification kill switches (master + per-audience) */}
      <NotificationToggleCard
        settings={settings}
        onToggle={toggleNotification}
      />

      {/* Service settings */}
      <div className="ky-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-base flex items-center gap-2"><Mail size={16}/>Mail Servisi</h3>
            <p className="text-xs text-[#64748B] mt-1">CRM'in e-postaları hangi servis üzerinden göndereceğini seçin.</p>
          </div>
          <button onClick={loadLogs} className="ky-btn ky-btn-outline ky-btn-sm"><Send size={11}/>Gönderim Logları</button>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <ServiceCard active={settings.active_service==="auto"} onClick={()=>setSettings({...settings, active_service:"auto"})} title="Otomatik" desc="Gmail bağlıysa Gmail, değilse Resend" badge={settings.active_service==="auto"?"Aktif":""}/>
          <ServiceCard active={settings.active_service==="gmail"} onClick={()=>setSettings({...settings, active_service:"gmail"})} title="Gmail (Google Workspace)" desc={settings.gmail_connected_users.length > 0 ? `${settings.gmail_connected_users.length} hesap bağlı` : "Bağlı hesap yok — Entegrasyonlar"} badge={settings.gmail_connected_users.length > 0 ? "Hazır" : "Eksik"}/>
          <ServiceCard active={settings.active_service==="resend"} onClick={()=>setSettings({...settings, active_service:"resend"})} title="Resend" desc={settings.resend_configured ? "API key ayarlı ✓" : "API key gerekli (3000/ay ücretsiz)"} badge={settings.resend_configured ? "Hazır" : "Eksik"}/>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Gönderen Adı</label><input value={settings.from_name||""} onChange={e=>setSettings({...settings, from_name:e.target.value})} className="ky-input" data-testid="mail-from-name"/></div>
          <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Gönderen E-posta</label><input value={settings.from_email||""} onChange={e=>setSettings({...settings, from_email:e.target.value})} className="ky-input" data-testid="mail-from-email"/></div>
        </div>
        {!settings.resend_configured && settings.active_service !== "gmail" && (
          <div className="mt-3 text-xs px-3 py-2 rounded-md bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]">
            Resend kullanmak için <strong>RESEND_API_KEY</strong> .env'e eklenmeli. <a className="underline" href="https://resend.com/api-keys" target="_blank" rel="noreferrer">resend.com → API Keys</a>'den ücretsiz key alın.
          </div>
        )}
        <button onClick={saveSettings} className="ky-btn ky-btn-accent mt-4" data-testid="save-mail-settings"><CheckCircle2 size={12}/>Kaydet</button>
      </div>

      {/* Templates */}
      <div className="ky-card p-5">
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2"><Edit2 size={15}/>E-posta Şablonları</h3>
        <p className="text-xs text-[#64748B] mb-3">Şablonları düzenlerken aşağıdaki <strong>dinamik alanları</strong> kullanabilirsiniz.</p>

        <details className="mb-4 group" data-testid="dynamic-vars-toggle">
          <summary className="cursor-pointer text-xs font-semibold text-[#4F46E5] hover:text-[#3730A3] select-none flex items-center gap-1.5 py-2">
            <span className="inline-block transition-transform group-open:rotate-90">▶</span>
            Tüm Dinamik Alanlar (Variables Reference)
          </summary>
          <div className="mt-3 grid sm:grid-cols-2 gap-4 p-4 bg-[#FAFAFB] rounded-lg border border-[#ECECEF]">
            {DYNAMIC_VAR_GROUPS.map(g => (
              <div key={g.title}>
                <div className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider mb-2">{g.title}</div>
                <div className="space-y-1.5">
                  {g.vars.map(v => (
                    <div key={v.key} className="flex items-start gap-2 text-[11px]">
                      <code className="font-mono bg-white border border-[#ECECEF] px-1.5 py-0.5 rounded text-[#4F46E5] whitespace-nowrap shrink-0">{`{{${v.key}}}`}</code>
                      <span className="text-[#64748B] leading-tight pt-0.5">{v.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="sm:col-span-2 pt-2 mt-1 border-t border-[#ECECEF] text-[11px] text-[#64748B]">
              <strong>İpucu:</strong> Koşullu alanlar için <code className="font-mono bg-white px-1 rounded text-[#4F46E5]">{"{{#alan}}içerik{{/alan}}"}</code> kullanın — alan boşsa o bölüm gizlenir.
            </div>
          </div>
        </details>

        <div className="flex gap-1 mb-3">
          <button onClick={()=>setAudience("customer")} className={`px-3 py-1.5 text-xs rounded-md ${audience==="customer"?"bg-[#4F46E5] text-white":"bg-[#F4F4F5] text-[#64748B]"}`} data-testid="audience-customer">Müşteriye Giden ({templates.filter(t=>t.audience==="customer").length})</button>
          <button onClick={()=>setAudience("team")} className={`px-3 py-1.5 text-xs rounded-md ${audience==="team"?"bg-[#4F46E5] text-white":"bg-[#F4F4F5] text-[#64748B]"}`} data-testid="audience-team">Ekip İçi ({templates.filter(t=>t.audience==="team").length})</button>
        </div>
        <div className="space-y-1.5">
          {filtered.map(t => (
            <div key={t.id} className="flex items-center gap-3 p-3 border border-[#ECECEF] rounded-lg hover:bg-[#FAFAFB]" data-testid={`tpl-${t.key}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{t.name}</div>
                <div className="text-[11px] text-[#94A3B8] truncate font-mono">{t.subject}</div>
              </div>
              <span className={`ky-badge ${t.enabled?"ky-badge-success":"ky-badge-neutral"}`}>{t.enabled?"Açık":"Kapalı"}</span>
              <button onClick={()=>setEditing(t)} className="ky-btn ky-btn-ghost ky-btn-icon" data-testid={`tpl-edit-${t.key}`}><Edit2 size={13}/></button>
            </div>
          ))}
        </div>
      </div>

      {/* Editor */}
      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={()=>setEditing(null)}>
          <div className="flex-1 bg-black/40"/>
          <div onClick={e=>e.stopPropagation()} className="w-full sm:w-[680px] bg-white flex flex-col ky-slide-in" data-testid="tpl-editor">
            <div className="px-5 py-4 border-b border-[#ECECEF] flex items-center justify-between">
              <div><div className="text-[10px] uppercase text-[#94A3B8] font-semibold">{editing.audience === "customer" ? "Müşteriye" : "Ekibe"}</div><h3 className="font-semibold">{editing.name}</h3></div>
              <button onClick={()=>setEditing(null)} className="ky-btn ky-btn-ghost ky-btn-icon"><Plus size={14} className="rotate-45"/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Anahtar (değiştirilemez)</label><input value={editing.key} disabled className="ky-input font-mono opacity-60"/></div>
              <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Şablon Adı</label><input value={editing.name} onChange={e=>setEditing({...editing,name:e.target.value})} className="ky-input"/></div>
              <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Konu</label><input value={editing.subject} onChange={e=>setEditing({...editing,subject:e.target.value})} className="ky-input" data-testid="tpl-subject"/></div>
              <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">HTML Gövde</label><textarea value={editing.body_html} onChange={e=>setEditing({...editing,body_html:e.target.value})} className="ky-textarea font-mono text-xs" rows={14} data-testid="tpl-body"/></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.enabled} onChange={e=>setEditing({...editing,enabled:e.target.checked})} data-testid="tpl-enabled"/>Şablon aktif (kapalıysa tetiklendiğinde e-posta gönderilmez)</label>
            </div>
            <div className="px-5 py-4 border-t border-[#ECECEF] flex gap-2"><button onClick={resetTemplate} className="ky-btn ky-btn-outline" data-testid="tpl-reset"><RotateCcw size={11}/>Varsayılan</button><div className="flex-1"/><button onClick={()=>setEditing(null)} className="ky-btn ky-btn-outline">İptal</button><button onClick={saveTemplate} className="ky-btn ky-btn-accent" data-testid="tpl-save">Kaydet</button></div>
          </div>
        </div>
      )}

      {showLogs && (
        <div className="fixed inset-0 z-50 flex" onClick={()=>setShowLogs(false)}>
          <div className="flex-1 bg-black/40"/>
          <div onClick={e=>e.stopPropagation()} className="w-full sm:w-[560px] bg-white flex flex-col ky-slide-in">
            <div className="px-5 py-4 border-b border-[#ECECEF] flex items-center justify-between"><h3 className="font-semibold">Gönderim Logları</h3><button onClick={()=>setShowLogs(false)} className="ky-btn ky-btn-ghost ky-btn-icon"><Plus size={14} className="rotate-45"/></button></div>
            <div className="flex-1 overflow-y-auto p-5 space-y-2">
              {logs.length === 0 && <div className="text-sm text-[#94A3B8] text-center py-6">Henüz e-posta gönderilmedi.</div>}
              {logs.map(l => (
                <div key={l.id} className="ky-card p-3 text-xs">
                  <div className="flex justify-between"><span className="font-medium">{l.to}</span><span className={`ky-badge ${l.status==="sent"?"ky-badge-success":"ky-badge-danger"}`}>{l.service || l.status}</span></div>
                  <div className="text-[#64748B] mt-1">{l.subject}</div>
                  <div className="text-[10px] text-[#94A3B8] mt-1 font-mono">{new Date(l.sent_at).toLocaleString("tr-TR")} · {l.template_key || "manual"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceCard({ active, onClick, title, desc, badge }) {
  return (
    <button onClick={onClick} className={`text-left p-3 rounded-lg border transition ${active?"border-[#4F46E5] bg-[#EEF2FF]":"border-[#ECECEF] hover:bg-[#FAFAFB]"}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">{title}</div>
        {badge && <span className={`ky-badge ${badge==="Hazır"||badge==="Aktif"?"ky-badge-success":"ky-badge-warning"}`}>{badge}</span>}
      </div>
      <div className="text-[11px] text-[#94A3B8] mt-1">{desc}</div>
    </button>
  );
}

function IntegrationsPanel() {
  const [google, setGoogle] = useState({ configured: false, connected: false, email: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiClient.get("/integrations/google/status").then(r => setGoogle(r.data)).catch(()=>{});
    if (new URLSearchParams(window.location.search).get("google") === "connected") {
      toast.success("Google Workspace bağlandı!");
      setTimeout(() => apiClient.get("/integrations/google/status").then(r => setGoogle(r.data)), 600);
    }
  }, []);

  const connect = async () => {
    setLoading(true);
    try { const { data } = await apiClient.get("/integrations/google/connect"); window.open(data.authorization_url, "_blank", "width=500,height=700"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Google bağlantısı kaldırılsın mı?")) return;
    await apiClient.post("/integrations/google/disconnect");
    setGoogle({ ...google, connected: false, email: null });
    toast.success("Bağlantı kaldırıldı");
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="integrations-panel">
      <div className="ky-card p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#4285F4] via-[#34A853] to-[#FBBC04] flex items-center justify-center text-white font-bold">G</div>
          <div className="flex-1"><h3 className="font-semibold text-[15px]">Google Workspace</h3><p className="text-xs text-[#64748B] mt-1">Calendar (Meet) + Gmail entegrasyonu</p></div>
          {google.connected && <span className="ky-badge ky-badge-success"><CheckCircle2 size={9}/>Bağlı</span>}
          {!google.connected && google.configured && <span className="ky-badge ky-badge-warning"><AlertCircle size={9}/>Bağlı değil</span>}
          {!google.configured && <span className="ky-badge ky-badge-neutral">Yapılandırılmadı</span>}
        </div>

        {!google.configured && <div className="text-xs px-3 py-2 rounded-md bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] mb-3">Sunucuda <code className="font-mono">GOOGLE_CLIENT_ID</code> ve <code className="font-mono">GOOGLE_CLIENT_SECRET</code> ayarlanmamış.</div>}
        {google.connected && <div className="text-xs px-3 py-2 rounded-md bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0] mb-3"><strong>{google.email}</strong> ile bağlı</div>}

        {/* Production publish reminder — Google OAuth apps in "Testing" mode block external Gmail accounts */}
        {google.configured && (
          <details className="mb-3 text-xs">
            <summary className="cursor-pointer text-[#4F46E5] font-medium select-none">⚠️ Google Cloud uygulaması "Testing" modunda mı? (öğrenmek için tıkla)</summary>
            <div className="mt-2 px-3 py-2.5 rounded-md bg-[#FEF2F2] text-[#7F1D1D] border border-[#FECACA] space-y-2">
              <p>Google Cloud Console'da OAuth uygulamanız <strong>Testing</strong> modundaysa yalnızca <strong>Test Users</strong> listesindeki e-postalar Google ile Bağlan yapabilir. Diğer ekip üyeleriniz "Access blocked: This app's request is invalid" hatası alır.</p>
              <p className="font-semibold">Çözüm seçenekleri:</p>
              <ol className="list-decimal ml-4 space-y-1">
                <li><strong>Hızlı:</strong> Console → OAuth consent screen → Test users → ekip e-postalarını ekle (max 100)</li>
                <li><strong>Kalıcı:</strong> Console → OAuth consent screen → <strong>PUBLISH APP</strong> butonuyla uygulamayı Production'a al. Bazı kapsamlar (Gmail send/read) için Google review gerekir (~3-5 iş günü).</li>
              </ol>
              <a href="https://console.cloud.google.com/apis/credentials/consent" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#4F46E5] font-medium underline mt-1">Google Cloud Console'u aç →</a>
            </div>
          </details>
        )}

        <ul className="text-xs text-[#64748B] space-y-1 mb-4">
          <li className="flex gap-2"><CheckCircle2 size={11} className="text-[#10B981] shrink-0 mt-0.5"/>Toplantılara otomatik Meet linki</li>
          <li className="flex gap-2"><CheckCircle2 size={11} className="text-[#10B981] shrink-0 mt-0.5"/>Eğitim derslerinde otomatik Meet odası</li>
          <li className="flex gap-2"><CheckCircle2 size={11} className="text-[#10B981] shrink-0 mt-0.5"/>Gmail üzerinden CRM e-posta gönderimi</li>
        </ul>

        {!google.connected ? (
          <button onClick={connect} disabled={!google.configured || loading} className="ky-btn ky-btn-accent w-full disabled:opacity-50" data-testid="google-connect">
            {loading ? <Loader2 size={12} className="animate-spin"/> : <Link2 size={12}/>}Google ile Bağlan
          </button>
        ) : (
          <button onClick={disconnect} className="ky-btn ky-btn-outline w-full text-[#EF4444] border-[#FECACA]" data-testid="google-disconnect"><Trash2 size={12}/>Bağlantıyı Kaldır</button>
        )}
      </div>

      <div className="ky-card p-5 bg-gradient-to-br from-[#FAFAFB] to-white">
        <div className="text-xs uppercase tracking-wider text-[#64748B] font-semibold mb-3">Yakında</div>
        <ul className="text-sm text-[#64748B] space-y-2">
          <li className="flex gap-2"><ExternalLink size={13} className="text-[#94A3B8] mt-0.5"/>WhatsApp Business — müşteri bildirimleri</li>
          <li className="flex gap-2"><ExternalLink size={13} className="text-[#94A3B8] mt-0.5"/>Google Sheets'e otomatik rapor</li>
          <li className="flex gap-2"><ExternalLink size={13} className="text-[#94A3B8] mt-0.5"/>Stripe — fatura ve ödeme tahsilatı</li>
        </ul>
      </div>
    </div>
  );
}


/* ============================================================================
   v14: Notification kill-switch card (master + per-audience)
   Sits at the top of the Mail panel so admins can suppress all auto-emails
   during historical data import or maintenance windows.
   ============================================================================ */
function NotificationToggleCard({ settings, onToggle }) {
  const master = settings.notifications_enabled !== false;
  const team = settings.team_notifications_enabled !== false;
  const customer = settings.customer_notifications_enabled !== false;

  // Master OFF visually dominates — show critical warning banner
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border p-5 transition-colors ${
        master
          ? "bg-gradient-to-br from-[#F0FDF4] via-white to-[#ECFDF5] border-[#A7F3D0]"
          : "bg-gradient-to-br from-[#FEF2F2] via-[#FFF7ED] to-[#FEF3C7] border-[#FCA5A5]"
      }`}
      data-testid="notification-toggle-card"
    >
      <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-20 blur-2xl"
           style={{ background: master ? "#10B981" : "#EF4444" }} />

      <div className="relative flex items-start gap-4">
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${
            master ? "bg-gradient-to-br from-[#10B981] to-[#059669]" : "bg-gradient-to-br from-[#EF4444] to-[#DC2626]"
          }`}
        >
          {master ? <Bell size={20} className="text-white" /> : <BellOff size={20} className="text-white" />}
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-bold text-base text-[#0F172A] flex items-center gap-2">
                Bildirim Kontrolü
                {!master && (
                  <span className="ky-badge ky-badge-danger text-[10px]">TÜM BİLDİRİMLER KAPALI</span>
                )}
              </h3>
              <p className="text-xs text-[#64748B] mt-0.5 max-w-2xl">
                Geçmiş veri girişi, bakım veya test sırasında otomatik e-postaları geçici olarak kapatın. Değişiklik anında etkili olur — kaydet butonuna gerek yok.
              </p>
            </div>
          </div>

          {/* Master kill switch */}
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            <BigToggle
              testid="toggle-master-notifications"
              checked={master}
              onChange={(v) => onToggle("notifications_enabled", v)}
              title="Tüm Bildirimler"
              subtitleOn="Aktif — e-postalar gönderilecek"
              subtitleOff="Kapalı — hiçbir e-posta gitmez"
              accent="#0F172A"
              prominent
            />
            <BigToggle
              testid="toggle-team-notifications"
              checked={team}
              onChange={(v) => onToggle("team_notifications_enabled", v)}
              disabled={!master}
              title="Ekip E-postaları"
              subtitleOn="Görev/toplantı/lead bildirimleri açık"
              subtitleOff="Ekip bildirimleri kapalı"
              accent="#4F46E5"
            />
            <BigToggle
              testid="toggle-customer-notifications"
              checked={customer}
              onChange={(v) => onToggle("customer_notifications_enabled", v)}
              disabled={!master}
              title="Müşteri E-postaları"
              subtitleOn="Karşılama / fatura / rapor / ders açık"
              subtitleOff="Müşteri/öğrenci bildirimleri kapalı"
              accent="#D97706"
            />
          </div>

          {!master && (
            <div className="mt-4 bg-white/70 backdrop-blur-sm border border-[#FCA5A5] rounded-lg p-3 text-xs text-[#7F1D1D] flex items-start gap-2" data-testid="notifications-off-warning">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <span>
                <strong>Tüm bildirimler kapalı.</strong> Onboarding adımları, toplantı/ders davetleri, fatura
                bildirimleri, rapor hazır e-postaları, görev atama bildirimleri ve tüm diğer otomatik
                e-postalar şu anda gönderilmiyor. Geçmiş veri girişi bittiğinde mutlaka tekrar açın.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BigToggle({ checked, onChange, disabled, title, subtitleOn, subtitleOff, accent, prominent, testid }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      data-testid={testid}
      className={`text-left p-3 rounded-xl border-2 transition-all ${
        disabled
          ? "opacity-50 cursor-not-allowed border-[#ECECEF] bg-[#FAFAFB]"
          : checked
          ? "border-[#A7F3D0] bg-white shadow-sm"
          : "border-[#FECACA] bg-white"
      } ${prominent ? "ring-2 ring-offset-1 " + (checked ? "ring-[#A7F3D0]" : "ring-[#FCA5A5]") : ""}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-sm font-bold text-[#0F172A] truncate" style={{ color: accent }}>{title}</span>
        <span
          className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
            checked ? "bg-[#10B981]" : "bg-[#94A3B8]"
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
              checked ? "translate-x-[18px]" : "translate-x-0.5"
            }`}
          />
        </span>
      </div>
      <p className="text-[11px] text-[#64748B] leading-tight">{checked ? subtitleOn : subtitleOff}</p>
    </button>
  );
}
